import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { DocusealState, Esign, Submitter } from './types';

const initialEsignState: Esign = {
  isLoaded: false,
  isFetching: false,
  isSaved: false,
  isPartySign: false,
  doc: {
    portalId: '',
    workspaceId: '',
    templateId: 0,
    bucketId: '',
    attachmentId: '',
    state: 'pending',
    submitters: [],
  },
  fileUrl: '',
  fileName: '',
  userEmail: '',
  token: '',
  mode: 'builder',
  signers: [],
  err: false,
  host: '',
};

export const $esign = createStore<Esign>(initialEsignState);

export const showSignersEvent = createEvent();
$esign.on(showSignersEvent, (state) => ({ ...state, mode: 'signer' }));
export const showBuilderEvent = createEvent();
$esign.on(showBuilderEvent, (state) => ({ ...state, mode: 'builder', err: false }));

export const updateSubmitterEvent = createEvent<{ role: string; userId: number }>();
$esign.on(updateSubmitterEvent, (state, payload) => {
  const { role, userId } = payload;
  const signer = state.signers.find((signer) => signer.userId === userId);
  const email = signer ? signer.email : '';
  const updatedSubmitters = state.doc.submitters.map((submitter) => {
    if (submitter.role === role) {
      return { ...submitter, userId, email };
    }
    return submitter;
  });
  return {
    ...state,
    doc: {
      ...state.doc,
      submitters: updatedSubmitters,
    },
  };
});

interface CreateDocusealParams {
  portalId: string;
  workspaceId: string;
  bucketId: string;
  attachmentId: string;
  templateId: number;
  token: string;
  portalHost: string;
}
export const createDocusealEvent = createEvent<{ templateId: number }>();
const startCreateDocusealEvent = createEvent();
$esign.on(startCreateDocusealEvent, (state) => ({
  ...state,
  isFetching: true,
}));
const createDocusealFx = createEffect(
  async ({ templateId, portalId, workspaceId, bucketId, attachmentId, token, portalHost }: CreateDocusealParams) => {
    const { data } = await axiosClient.post<unknown>(`${generalEnv.nextApiPath}/esign/create`, {
      templateId,
      portalId,
      workspaceId,
      bucketId,
      attachmentId,
      token,
      portalHost,
    });
    return data;
  }
);
$esign.on(createDocusealFx.doneData, (esign, data) => {
  return Object.assign({}, esign, {
    isLoaded: true,
    isFetching: false,
    doc: data,
  });
});
$esign.on(createDocusealFx.failData, (esign, data) => {
  console.error('Fail to create docuseal', data);
  return Object.assign({}, esign, {
    isFetching: false,
  });
});
sample({
  source: $esign,
  clock: createDocusealEvent,
  filter: (esign) => !esign.isFetching,
  fn: (esign, { templateId }: { templateId: number }) => ({
    templateId,
    portalId: esign.doc.portalId,
    workspaceId: esign.doc.workspaceId,
    bucketId: esign.doc.bucketId,
    attachmentId: esign.doc.attachmentId,
    token: esign.token,
    portalHost: esign.host,
  }),
  target: [startCreateDocusealEvent, createDocusealFx],
});

interface UpdateDocusealParams {
  bucketId: string;
  attachmentId: string;
  state: DocusealState;
  submitters: Submitter[];
}
export const updateDocusealEvent = createEvent<{ state: DocusealState; submitters: Submitter[] }>();
const startUpdateDocusealEvent = createEvent();
$esign.on(startUpdateDocusealEvent, (state) => ({
  ...state,
  isFetching: true,
}));
const updateDocusealFx = createEffect(async ({ bucketId, attachmentId, state, submitters }: UpdateDocusealParams) => {
  const { data } = await axiosClient.post<unknown>(`${generalEnv.nextApiPath}/esign/update`, {
    bucketId,
    attachmentId,
    state,
    submitters,
  });
  return data;
});
$esign.on(updateDocusealFx.doneData, (esign, data) => {
  return Object.assign({}, esign, {
    isLoaded: true,
    isFetching: false,
    doc: data,
  });
});
$esign.on(updateDocusealFx.failData, (esign, data) => {
  console.error('Fail to update docuseal', data);
  return Object.assign({}, esign, {
    isFetching: false,
  });
});
sample({
  source: $esign,
  clock: updateDocusealEvent,
  filter: (esign) => !esign.isFetching,
  fn: (esign, { state, submitters }) => {
    const mergedSubmitters = submitters.map((newSubmitter) => {
      const existingSubmitter = esign.doc.submitters.find((sub) => sub.role === newSubmitter.role);
      return {
        ...existingSubmitter,
        ...newSubmitter,
        userId: newSubmitter.userId || existingSubmitter?.userId || 0,
        email: newSubmitter.email || existingSubmitter?.email || '',
      };
    });

    return {
      bucketId: esign.doc.bucketId,
      attachmentId: esign.doc.attachmentId,
      state,
      submitters: mergedSubmitters,
    };
  },
  target: [startUpdateDocusealEvent, updateDocusealFx],
});
sample({
  source: $esign,
  clock: updateSubmitterEvent,
  fn: (esign) => ({
    state: esign.doc.state,
    submitters: esign.doc.submitters,
  }),
  target: updateDocusealEvent,
});

export const saveDocusealSubmittersEvent = createEvent();
const startSaveDocusealSubmittersEvent = createEvent();
$esign.on(startSaveDocusealSubmittersEvent, (state) => ({
  ...state,
  isSaved: false,
  isFetching: true,
}));
interface SaveDocusealParams {
  bucketId: string;
  attachmentId: string;
  templateId: number;
  submitters: Submitter[];
}
const saveDocusealFx = createEffect(async ({ bucketId, attachmentId, templateId, submitters }: SaveDocusealParams) => {
  const { data } = await axiosClient.post<unknown>(`${generalEnv.nextApiPath}/esign/save`, {
    bucketId,
    attachmentId,
    templateId,
    submitters,
  });
  return data;
});
$esign.on(saveDocusealFx.doneData, (esign, data) => {
  return Object.assign({}, esign, {
    isLoaded: true,
    isFetching: false,
    isSaved: true,
    doc: data,
    err: false,
  });
});
$esign.on(saveDocusealFx.failData, (esign, data) => {
  console.error('Fail to save docuseal', data);
  return Object.assign({}, esign, {
    isFetching: false,
    isSaved: true,
    err: true,
  });
});
sample({
  source: $esign,
  clock: saveDocusealSubmittersEvent,
  filter: (esign) => !esign.isFetching,
  fn: (esign) => ({
    bucketId: esign.doc.bucketId,
    attachmentId: esign.doc.attachmentId,
    templateId: esign.doc.templateId,
    submitters: esign.doc.submitters,
  }),
  target: [startSaveDocusealSubmittersEvent, saveDocusealFx],
});

export const signDocusealPartyEvent = createEvent<{ userId: number }>();
const startSignDocusealPartyEvent = createEvent();
$esign.on(startSignDocusealPartyEvent, (state) => ({
  ...state,
  isPartySign: false,
  isFetching: true,
}));
interface SignDocusealPartyParams {
  bucketId: string;
  attachmentId: string;
  submitters: Submitter[];
  state: DocusealState;
}
const signDocusealPartyFx = createEffect(
  async ({ bucketId, attachmentId, submitters, state }: SignDocusealPartyParams) => {
    const { data } = await axiosClient.post<unknown>(`${generalEnv.nextApiPath}/esign/sign`, {
      bucketId,
      attachmentId,
      state,
      submitters,
    });
    return data;
  }
);
$esign.on(signDocusealPartyFx.doneData, (esign, data) => {
  return Object.assign({}, esign, {
    isLoaded: true,
    isFetching: false,
    isPartySign: true,
    doc: data,
    err: false,
  });
});
$esign.on(signDocusealPartyFx.failData, (esign, data) => {
  console.error('Fail to save docuseal', data);
  return Object.assign({}, esign, {
    isFetching: false,
    isPartySign: true,
    err: true,
  });
});
sample({
  source: $esign,
  clock: signDocusealPartyEvent,
  filter: (esign) => !esign.isFetching,
  fn: (esign, { userId }) => {
    const updatedSubmitters: Submitter[] = esign.doc.submitters.map((submitter) =>
      submitter.userId === userId ? { ...submitter, state: 'complete' } : submitter
    );
    const allComplete = updatedSubmitters.every((submitter) => submitter.state === 'complete');
    const updatedDocState = allComplete ? 'complete' : esign.doc.state;
    return {
      bucketId: esign.doc.bucketId,
      attachmentId: esign.doc.attachmentId,
      submitters: updatedSubmitters,
      state: updatedDocState,
    };
  },
  target: [startSignDocusealPartyEvent, signDocusealPartyFx],
});

export const getDocumentsEvent = createEvent();
interface GetDocumentsParams {
  bucketId: string;
  attachmentId: string;
}
const getDocumentsFx = createEffect(async ({ bucketId, attachmentId }: GetDocumentsParams) => {
  const { data } = await axiosClient.post<unknown>(`${generalEnv.nextApiPath}/esign/documents`, {
    bucketId,
    attachmentId,
  });
  return data;
});
$esign.on(getDocumentsFx.doneData, (esign, data) => {
  return Object.assign({}, esign, {
    documents: data,
  });
});
sample({
  source: $esign,
  clock: getDocumentsEvent,
  fn: (esign) => {
    return {
      bucketId: esign.doc.bucketId,
      attachmentId: esign.doc.attachmentId,
    };
  },
  target: getDocumentsFx,
});
